import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Home,
  Sparkles,
  CalendarCheck,
  MessageSquare,
  Menu,
  X,
  Mail,
  ArrowUpRight,
  Instagram,
  Facebook,
  Linkedin,
  LogIn,
  LayoutDashboard,
} from "lucide-react";
import { BRAND } from "@/lib/data";
import Newsletter from "@/components/Newsletter";
import Logo from "@/components/Logo";
import CookieBanner from "@/components/CookieBanner";
import ChatWidget from "@/components/ChatWidget";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/book", label: "Book" },
  { to: "/intake", label: "Intake" },
  { to: "/insights", label: "Insights" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const portalPath = user?.role === "client" ? "/portal" : (user?.role === "admin" || user?.role === "team_member") ? "/admin" : null;
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-soft/80 border-b border-navy/5"
    >
      <div className="container-app flex items-center justify-between py-4">
        <Logo surface="light" testid="header-logo" />
        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              end={item.to === "/"}
              className={({ isActive }) =>
                `nav-link ${isActive ? "text-navy font-medium" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          {portalPath ? (
            <Link to={portalPath} data-testid="header-portal-link" className="btn-secondary">
              <LayoutDashboard className="w-4 h-4" /> {user.role === "client" ? "My Portal" : "Admin"}
            </Link>
          ) : (
            <Link to="/login" data-testid="header-login-link" className="nav-link inline-flex items-center gap-1.5">
              <LogIn className="w-4 h-4" /> Sign In
            </Link>
          )}
          <Link
            to="/book"
            data-testid="header-book-cta"
            className="btn-primary"
          >
            Book Appointment <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 text-navy"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-navy/10 bg-soft"
        >
          <div className="container-app py-4 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-3 px-2 rounded-lg text-sm tracking-wide ${
                    isActive
                      ? "bg-navy/5 text-navy font-medium"
                      : "text-navy/80 hover:bg-navy/5"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              data-testid="mobile-book-cta"
              className="btn-primary mt-3 justify-center"
            >
              Book Appointment
            </Link>
            {portalPath ? (
              <Link to={portalPath} onClick={() => setOpen(false)} data-testid="mobile-portal-link" className="btn-secondary mt-2 justify-center">
                {user.role === "client" ? "My Portal" : "Admin Console"}
              </Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-login-link" className="btn-secondary mt-2 justify-center">
                <LogIn className="w-4 h-4" /> Sign In
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MobileTabBar() {
  const location = useLocation();
  const tabs = [
    { to: "/", icon: Home, label: "Home" },
    { to: "/services", icon: Sparkles, label: "Services" },
    { to: "/book", icon: CalendarCheck, label: "Book" },
    { to: "/contact", icon: MessageSquare, label: "Contact" },
  ];
  return (
    <nav
      data-testid="mobile-tab-bar"
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-navy/10 shadow-[0_-4px_20px_rgb(0,0,0,0.05)]"
    >
      <div className="grid grid-cols-4 h-16">
        {tabs.map(({ to, icon: Icon, label }) => {
          const active =
            to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
          return (
            <Link
              key={to}
              to={to}
              data-testid={`tab-${label.toLowerCase()}`}
              className={`flex flex-col items-center justify-center gap-1 text-[11px] tracking-wide ${
                active ? "text-gold" : "text-navy/60"
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={active ? 2.2 : 1.6} />
              <span>{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-navy text-soft mt-20">
      <div className="container-app py-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <Logo surface="dark" testid="footer-logo" />
          <p className="mt-5 text-soft/70 text-sm leading-relaxed max-w-sm">
            An ecosystem of services helping entrepreneurs, nonprofits, and small
            businesses build stronger financial systems and lasting opportunities.
          </p>
          <div className="mt-6 flex items-center gap-4 text-soft/60">
            {BRAND.social.instagram && (
              <a
                href={BRAND.social.instagram}
                target="_blank" rel="noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="hover:text-gold transition"
              >
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {BRAND.social.facebook && (
              <a
                href={BRAND.social.facebook}
                target="_blank" rel="noreferrer"
                data-testid="footer-facebook"
                aria-label="Facebook"
                className="hover:text-gold transition"
              >
                <Facebook className="w-5 h-5" />
              </a>
            )}
            {BRAND.social.linkedin && (
              <a
                href={BRAND.social.linkedin}
                target="_blank" rel="noreferrer"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
                className="hover:text-gold transition"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-soft text-base font-serif mb-4">Explore</h4>
          <ul className="space-y-2 text-sm text-soft/70">
            {NAV_ITEMS.map((i) => (
              <li key={i.to}>
                <Link
                  to={i.to}
                  data-testid={`footer-link-${i.label.toLowerCase()}`}
                  className="hover:text-gold transition"
                >
                  {i.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-soft text-base font-serif mb-4">Services</h4>
          <ul className="space-y-2 text-sm text-soft/70">
            <li><Link to="/services/bookkeeping" className="hover:text-gold transition">Bookkeeping</Link></li>
            <li><Link to="/services/business-formation" className="hover:text-gold transition">Business Formation</Link></li>
            <li><Link to="/services/consulting" className="hover:text-gold transition">Business Consulting</Link></li>
            <li><Link to="/services/taxes" className="hover:text-gold transition">Divinity Taxes</Link></li>
            <li><Link to="/services/travel" className="hover:text-gold transition">Travel Services</Link></li>
            <li><Link to="/services/notary" className="hover:text-gold transition">Notary</Link></li>
            <li><Link to="/services/credit-repair" className="hover:text-gold transition">Anew Credit Repair</Link></li>
            <li><Link to="/services/business-credit" className="hover:text-gold transition">Business Credit</Link></li>
            <li><Link to="/services/money-mindset" className="hover:text-gold transition">Money Mindset Reset™</Link></li>
            <li><Link to="/services/admin-services" className="hover:text-gold transition">Administrative</Link></li>
            <li><Link to="/services/real-estate" className="hover:text-gold transition">Real Estate <span className="text-[10px] uppercase tracking-widest text-gold">Soon</span></Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-soft text-base font-serif mb-4">Stay Connected</h4>
          <p className="text-sm text-soft/70 mb-4">
            Monthly insights on financial wellness, business growth, and ConnectHer offerings.
          </p>
          <Newsletter variant="dark" source="footer" />
          <div className="mt-6 text-sm text-soft/60 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <a href={`mailto:${BRAND.email}`} className="hover:text-gold transition">
              {BRAND.email}
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-soft/10">
        <div className="container-app py-6 flex flex-col md:flex-row gap-2 items-center justify-between text-xs text-soft/50">
          <span>© {new Date().getFullYear()} ConnectHer HQ. All rights reserved.</span>
          <span className="tracking-[0.25em] uppercase text-gold/80">Build · Connect · Empower</span>
          <span className="italic">“The Divine Connector”</span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-soft">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <MobileTabBar />
      <CookieBanner />
      <ChatWidget />
    </div>
  );
}
